import dayjs from "dayjs";

// ===== Helpers de días hábiles (espejo de useTribunalProcedimiento) =====
const _isWeekend = (d) => { const day = d.day(); return day === 0 || day === 6; };

const _addBusinessDays = (start, businessDays) => {
  if (!start || !Number.isFinite(Number(businessDays))) return null;
  let d = dayjs(start).startOf("day");
  let remaining = Number(businessDays);
  while (remaining > 0) { d = d.add(1, "day"); if (!_isWeekend(d)) remaining -= 1; }
  return d;
};

const _businessDaysLeft = (from, to) => {
  if (!from || !to) return null;
  const a = dayjs(from).startOf("day");
  const b = dayjs(to).startOf("day");
  if (b.isSame(a, "day") || b.isBefore(a, "day")) return 0;
  let count = 0;
  let d = a.add(1, "day");
  while (d.isBefore(b, "day") || d.isSame(b, "day")) { if (!_isWeekend(d)) count += 1; d = d.add(1, "day"); }
  return count;
};

const _terminoVigenteEtapa = (exp, etapaNum, today) => {
  const fecha = exp[`etapa${etapaNum}_fecha_notif`];
  const dias = exp[`etapa${etapaNum}_dias_habiles`];
  if (!fecha || dias == null) return false;
  const deadline = _addBusinessDays(fecha, Number(dias));
  if (!deadline) return false;
  const diasLeft = _businessDaysLeft(today, deadline);
  return diasLeft !== null && diasLeft > 0;
};

// Mismo catálogo fijo (id = índice + 1) que usa TabTerminacion.jsx como
// respaldo cuando el catálogo `forma_terminacion` del backend no resuelve.
export const FORMA_TERMINACION_NOMBRES = [
  "Convenio",
  "Desistimiento",
  "Sentencia / Laudo favorable",
  "Sentencia / Laudo desfavorable",
  "Caducidad",
  "Sobreseimiento",
  "Incompetencia",
  "Acumulación a otro expediente",
];

// Nombres planos (sin el prefijo "Etapa N ·") para el badge de la esquina de
// cada tarjeta — ahí solo se muestra el nombre de lo que es, no el número.
export const ETAPA_NOMBRES = {
  1: "Presentación de Demanda",
  3: "Admisión",
  4: "Emplazamiento",
  5: "Contestación",
  6: "Reconvención",
  7: "Réplica",
  8: "Contrarréplica",
  9: "Integración de la Litis",
  10: "Audiencia Conciliatoria",
  11: "Audiencia Preliminar",
  12: "Audiencia de Juicio",
};

// Etapa real en la que está el expediente, derivada de los datos que ya tiene
// capturados (no un genérico "en proceso"): se busca la etapa más avanzada con
// fecha registrada y se cae hacia atrás hasta la 1 (presentación de demanda).
// Las etapas de audiencia (10-12) usan el mismo amarillo que "Próximas
// Audiencias" (a lo que pertenecen); el resto usa gris neutro.
const computeEtapaReal = (exp) => {
  const build = (num) => ({
    num,
    label: ETAPA_NOMBRES[num].toUpperCase(),
    kind: num >= 10 ? "yellow" : num === 1 ? "red" : "gray",
  });

  // Etapa más avanzada detectada por el sistema viejo de etapas (tribunal_etapas).
  let numViejo = 1;
  if (exp?.fecha_audiencia_etapa12) numViejo = 12;
  else if (exp?.fecha_audiencia_etapa11) numViejo = 11;
  else if (exp?.fecha_audiencia_conciliadora) numViejo = 10;
  else {
    for (const num of [9, 8, 7, 6, 5, 4]) {
      if (exp?.[`etapa${num}_fecha_notif`]) { numViejo = num; break; }
    }
    if (numViejo === 1 && exp?.fecha_radicacion) numViejo = 3;
  }

  // Etapa más avanzada registrada en el tab "Expediente" (tribunal_actuacion),
  // que es el flujo que reemplazó al de arriba y no siempre llena esos campos.
  const numNuevo = Number(exp?.etapa_actuacion_max) || 1;

  return build(Math.max(numViejo, numNuevo));
};

// Misma lógica de badge de etapa/estatus que usa el listado de Tribunal
// (laboralTribunalPage.jsx), extraída aquí para que el detalle
// (LaboralTribunalDetallePage.jsx) muestre exactamente el mismo badge.
export const computeTribunalEstatus = (exp) => {
  const today = dayjs().startOf("day");
  const _esFutura = (f) => {
    const d = dayjs(f).startOf("day");
    return d.isValid() && (d.isSame(today) || d.isAfter(today));
  };

  if (exp?.active === 0 || exp?.active === "0") {
    return { label: "ARCHIVADO", kind: "gray" };
  }

  // Concluido
  if (exp?.id_forma_terminacion) {
    const rawNombre = String(exp?.forma_terminacion_nombre || "").trim();
    // El backend cae a CAST(id AS CHAR) cuando el catálogo no resuelve el
    // nombre (COALESCE en la query). Si llega el número crudo, lo traducimos
    // con el mismo catálogo fijo que usa TabTerminacion.jsx (id = índice+1).
    const esSoloNumero = /^\d+$/.test(rawNombre);
    const nombreResuelto =
      !rawNombre || esSoloNumero
        ? FORMA_TERMINACION_NOMBRES[Number(exp.id_forma_terminacion) - 1] || rawNombre || "Concluido"
        : rawNombre;
    return {
      label: nombreResuelto.toUpperCase(),
      kind: "green",
    };
  }

  // Término crítico vigente (etapas 4-9)
  for (const num of [4, 5, 6, 7, 8, 9]) {
    if (_terminoVigenteEtapa(exp, num, today)) {
      return { label: ETAPA_NOMBRES[num].toUpperCase(), kind: "red" };
    }
  }

  // Término crítico capturado en la bitácora de Actuaciones (tab
  // "Expediente") — fuente que reemplazó a las etapas viejas para registrar
  // términos nuevos, así que no siempre queda reflejado ahí.
  if (exp?.actuacion_termino_fecha_limite && _esFutura(exp.actuacion_termino_fecha_limite)) {
    return { label: (exp.actuacion_termino_tipo || "Término").toUpperCase(), kind: "red" };
  }

  // Próxima audiencia (etapas 10-12)
  if (exp?.fecha_audiencia_conciliadora && _esFutura(exp.fecha_audiencia_conciliadora))
    return { label: ETAPA_NOMBRES[10].toUpperCase(), kind: "yellow" };
  if (exp?.fecha_audiencia_etapa11 && _esFutura(exp.fecha_audiencia_etapa11))
    return { label: ETAPA_NOMBRES[11].toUpperCase(), kind: "yellow" };
  if (exp?.fecha_audiencia_etapa12 && _esFutura(exp.fecha_audiencia_etapa12))
    return { label: ETAPA_NOMBRES[12].toUpperCase(), kind: "yellow" };

  // Término por fecha_limite_contestacion (carga rápida o campo directo)
  if (exp?.fecha_limite_contestacion && _esFutura(exp.fecha_limite_contestacion))
    return { label: "LÍMITE CONTESTACIÓN", kind: "red" };

  // Sin nada crítico/próximo vigente: se muestra la etapa real en la que va el
  // expediente (nunca un genérico "en proceso" ni "pendiente de información").
  const etapaReal = computeEtapaReal(exp);
  return { label: etapaReal.label, kind: etapaReal.kind };
};

// Términos críticos vigentes (aún no vencidos) del expediente: los de las
// etapas viejas (4-9, tribunal_etapas) más el capturado en la bitácora de
// Actuaciones del tab "Expediente" (tribunal_actuaciones) — misma fuente que
// usa el badge de arriba, pero aquí se listan todos, no solo el primero.
export const getTerminosCriticos = (exp) => {
  if (!exp) return [];
  const today = dayjs().startOf("day");
  const out = [];

  for (const num of [4, 5, 6, 7, 8, 9]) {
    const fecha = exp[`etapa${num}_fecha_notif`];
    const dias = exp[`etapa${num}_dias_habiles`];
    if (!fecha || dias == null) continue;
    const deadline = _addBusinessDays(fecha, Number(dias));
    if (deadline && !deadline.isBefore(today, "day")) {
      out.push({ label: ETAPA_NOMBRES[num], fecha: deadline.format("YYYY-MM-DD") });
    }
  }

  if (exp?.actuacion_termino_fecha_limite) {
    const d = dayjs(exp.actuacion_termino_fecha_limite).startOf("day");
    if (d.isValid() && !d.isBefore(today, "day")) {
      out.push({ label: exp.actuacion_termino_tipo || "Término", fecha: exp.actuacion_termino_fecha_limite });
    }
  }

  out.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  return out;
};

// Próxima audiencia agendada (conciliatoria/preliminar/juicio), la que esté
// más cercana en el tiempo; si todas ya pasaron, se regresa la más reciente
// como referencia en vez de no mostrar nada.
export const getProximaAudienciaTribunal = (exp) => {
  if (!exp) return null;
  const candidatas = [
    { label: ETAPA_NOMBRES[10], fecha: exp?.fecha_audiencia_conciliadora },
    { label: ETAPA_NOMBRES[11], fecha: exp?.fecha_audiencia_etapa11 },
    { label: ETAPA_NOMBRES[12], fecha: exp?.fecha_audiencia_etapa12 },
  ].filter((c) => c.fecha && dayjs(c.fecha).isValid());
  if (!candidatas.length) return null;

  const today = dayjs().startOf("day");
  const futuras = candidatas.filter((c) => !dayjs(c.fecha).startOf("day").isBefore(today));
  const lista = (futuras.length ? futuras : candidatas)
    .slice()
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  return lista[0];
};

// Mismos colores hex que usa el badge de estatus en Conciliación
// (LaboralConciliacionDetallePage.jsx) y en el listado de Tribunal.
export const STATUS_PILL_HEX = {
  green: "#16A34A",
  red: "#DC2626",
  blue: "#1677ff",
  yellow: "#F59E0B",
  gray: "#6B7280",
  primary: "#0A539A",
};
